/** Admin rifle-scoring routes. The list and each shooter's score form are separate pages so back/forward work. */

export const SCORING_LIST_ROUTE = '/admin/scoring';
export const SCORING_TOURNAMENT_ROUTE = '/admin/scoring/:slug';
export const SCORING_CHECK_IN_ROUTE = '/admin/scoring/:slug/check-in';
export const SCORING_CHECK_IN_CALLER_ROUTE = '/admin/scoring/:slug/check-in/caller';
export const SCORING_SHOOTER_ROUTE = '/admin/scoring/:slug/shooter/:shooterKey';
export const SCORING_ROUND1_TEAMS_ROUTE = '/admin/scoring/:slug/teams';
export const SCORING_ROUND2_ROUTE = '/admin/scoring/:slug/round2';
export const SCORING_ROUND2_SHOOTER_ROUTE = '/admin/scoring/:slug/round2/shooter/:shooterKey';

export interface ScoringShooterRef {
  id: string;
  name: string;
  bib: string | null;
}

/** Router state the list page attaches, so the shooter page knows Back can use history. */
export interface ScoringFromListState {
  fromList: true;
}

export function isFromListState(state: unknown): state is ScoringFromListState {
  return typeof state === 'object' && state !== null && (state as { fromList?: unknown }).fromList === true;
}

/** The URL key for a shooter: their RFL number when they have one, else their id (names added by hand). */
export function shooterKey(shooter: ScoringShooterRef): string {
  const bib = shooter.bib?.trim();
  return bib ? bib : shooter.id;
}

function withQuery(path: string, query?: string | null, group?: string | null, gender?: string | null): string {
  const params = new URLSearchParams();
  const q = query?.trim();
  if (q) params.set('q', q);
  if (group === '1' || group === '2' || group === '3') params.set('group', group);
  if (gender === 'male' || gender === 'female' || gender === 'teams') params.set('gender', gender);
  const text = params.toString();
  return text ? `${path}?${text}` : path;
}

export function scoringListPath(slug: string, query?: string | null, group?: string | null, gender?: string | null): string {
  return withQuery(`${SCORING_LIST_ROUTE}/${encodeURIComponent(slug)}`, query, group, gender);
}

export function scoringCheckInPath(slug: string): string {
  return `${SCORING_LIST_ROUTE}/${encodeURIComponent(slug)}/check-in`;
}

export function scoringCheckInCallerPath(slug: string): string {
  return `${scoringCheckInPath(slug)}/caller`;
}

export function scoringRound1TeamsPath(slug: string): string {
  return `${SCORING_LIST_ROUTE}/${encodeURIComponent(slug)}/teams`;
}

export function scoringRound2Path(slug: string): string {
  return `${SCORING_LIST_ROUTE}/${encodeURIComponent(slug)}/round2`;
}

export function scoringRound2ShooterPath(slug: string, shooter: ScoringShooterRef): string {
  return `${scoringRound2Path(slug)}/shooter/${encodeURIComponent(shooterKey(shooter))}`;
}

export function scoringShooterPath(
  slug: string,
  shooter: ScoringShooterRef,
  query?: string | null,
  group?: string | null,
  gender?: string | null,
): string {
  return withQuery(
    `${SCORING_LIST_ROUTE}/${encodeURIComponent(slug)}/shooter/${encodeURIComponent(shooterKey(shooter))}`,
    query,
    group,
    gender,
  );
}

/** Match an RFL number (any case) first, then a shooter id. */
export function findShooterByKey<T extends ScoringShooterRef>(shooters: readonly T[], key: string): T | null {
  const wanted = key.trim().toLowerCase();
  if (!wanted) return null;
  return (
    shooters.find((shooter) => (shooter.bib ?? '').trim().toLowerCase() === wanted) ??
    shooters.find((shooter) => shooter.id === key) ??
    null
  );
}

export function filterShooters<T extends ScoringShooterRef>(shooters: readonly T[], query?: string | null): T[] {
  const needle = query?.trim().toLowerCase() ?? '';
  if (!needle) return [...shooters];
  return shooters.filter(
    (shooter) => shooter.name.toLowerCase().includes(needle) || (shooter.bib ?? '').toLowerCase().includes(needle),
  );
}

/** The shooter after this one in the (searched) list, or null at the end. */
export function nextShooter<T extends ScoringShooterRef>(
  shooters: readonly T[],
  currentId: string,
  query?: string | null,
): T | null {
  const list = filterShooters(shooters, query);
  const index = list.findIndex((shooter) => shooter.id === currentId);
  if (index === -1) return list.find((shooter) => shooter.id !== currentId) ?? null;
  return list[index + 1] ?? null;
}

const SCROLL_KEY = 'smym:scoring-list-scroll';

/** Remember where the list was scrolled before opening a shooter. */
export function rememberListScroll(slug: string, y: number): void {
  try {
    window.sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ slug, y: Math.max(0, Math.round(y)) }));
  } catch {
    // Private mode or storage full: Back still works, just from the top.
  }
}

export function takeListScroll(slug: string): number | null {
  try {
    const raw = window.sessionStorage.getItem(SCROLL_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(SCROLL_KEY);
    const saved = JSON.parse(raw) as { slug?: unknown; y?: unknown };
    return saved.slug === slug && typeof saved.y === 'number' ? saved.y : null;
  } catch {
    return null;
  }
}
